import { InputLabel, MenuItem } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Service } from 'shell/models/service';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';

interface ServiceListComponentProps {
  disabled?: boolean;
  value: string;
  onChange: (acronym?: string) => void;
  error?: boolean;
  label?: string;
  id?: string;
  required?: boolean;
}

const ServiceListComponent = ({
  disabled,
  onChange,
  required,
  value,
  error,
  label = 'Servizio',
  id = 'service-list',
}: ServiceListComponentProps): React.ReactElement => {
  const [service] = useHttpFetch<HttpResponseList<Service>, Array<ItemVM>>(
    `${__MANAGEMENT_API_URL__}/servizi`,
    {
      onBootstrap: true,
      initialData: [],
      options: {
        loading: true,
        errorCatching: true,
        params: {
          filters: `gestioneCassa:S:${Operation.EQUALS}`,
          sort: 'descrizione:ASC',
        },
      },
      transformer: (response: AxiosResponse<HttpResponseList<Service>>) => {
        const { data } = response;

        return data?.payload.list.map((item) => ({
          id: item.key.codiceServizio,
          value: item.descrizione,
        })) as Array<ItemVM>;
      },
    }
  );

  const list = (service.data as Array<ItemVM>) || [];

  const idTag = `${id}-${label}`;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} id={idTag}>
        {required ? `${label} *` : label}
      </InputLabel>
      <PmiSelect
        labelId={idTag}
        id={id}
        name={id}
        value={value}
        label={required ? `${label} *` : label}
        onChange={(event) => {
          const selectedValue = event.target.value;
          onChange(selectedValue);
        }}
        disabled={disabled}
        error={error}
      >
        <MenuItem value="ALL">
          <em>Tutti i servizi</em>
        </MenuItem>
        {list.map((item: ItemVM) => (
          <MenuItem key={item.id} value={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ServiceListComponent.displayName = 'Service List component';

const ServiceList = memo(ServiceListComponent);

export default ServiceList as typeof ServiceListComponent;
